/**
 * Password Hashing Utility for React Native/Expo
 * Uses expo-crypto for cross-platform password hashing
 * 
 * Note: This is a simplified implementation for React Native.
 * For production, consider using a more secure implementation
 * or server-side password hashing.
 */

import * as Crypto from 'expo-crypto';

/**
 * Generate a random salt
 */
const generateSalt = async (): Promise<string> => {
  const randomBytes = await Crypto.getRandomBytesAsync(16);
  return Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Hash a password with a salt using SHA-256
 * Format: salt:hash
 */
export const hashPassword = async (password: string): Promise<string> => {
  console.log('🔐 Hashing password...');
  const salt = await generateSalt();
  console.log('🧂 Salt generated, length:', salt.length);
  
  const hash = await Crypto.digestStringAsync(
    Crypto.CryptoDigestAlgorithm.SHA256,
    salt + password
  );
  console.log('🔒 Hash generated, length:', hash.length);
  
  const result = `${salt}:${hash}`;
  console.log('✅ Final hash format:', `${salt.substring(0, 8)}...${salt.substring(salt.length - 8)}:${hash.substring(0, 8)}...`);
  console.log('📏 Total hash length:', result.length);
  
  return result;
};

/**
 * Compare a plain text password with a hashed password
 * @param password Plain text password
 * @param hashedPassword Hashed password in format "salt:hash"
 */
export const comparePassword = async (
  password: string,
  hashedPassword: string
): Promise<boolean> => {
  try {
    console.log('🔍 Comparing password...');
    console.log('📝 Stored hash length:', hashedPassword?.length);
    
    const [salt, originalHash] = hashedPassword.split(':');
    
    if (!salt || !originalHash) {
      console.error('❌ Invalid hash format - missing salt or hash');
      console.error('Salt:', salt ? 'exists' : 'missing');
      console.error('Hash:', originalHash ? 'exists' : 'missing');
      return false;
    }
    
    console.log('🧂 Salt extracted, length:', salt.length);
    console.log('🔒 Original hash length:', originalHash.length);
    
    const hash = await Crypto.digestStringAsync(
      Crypto.CryptoDigestAlgorithm.SHA256,
      salt + password
    );
    
    console.log('🔐 Computed hash length:', hash.length);
    console.log('🎯 Hashes match:', hash === originalHash);
    
    return hash === originalHash;
  } catch (error) {
    console.error('❌ Error comparing passwords:', error);
    return false;
  }
};

/**
 * Validate password strength
 */
export const validatePasswordStrength = (password: string): {
  isValid: boolean;
  message: string;
} => {
  if (password.length < 8) {
    return {
      isValid: false,
      message: 'Password must be at least 8 characters long',
    };
  }
  
  // Optional: Add more strength requirements
  // const hasUpperCase = /[A-Z]/.test(password);
  // const hasLowerCase = /[a-z]/.test(password);
  // const hasNumbers = /\d/.test(password);
  // const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  return {
    isValid: true,
    message: 'Password is valid',
  };
};
